import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal, Spinner } from "reactstrap";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { toast } from "react-toastify";
import ErrorMessage from "../../../components/comman/ErrorMessage";
import { UnitCollection } from "../../../utility/CommonFunctions";
import {
  ITEM_CATEGORIES,
  GST_OPTIONS,
  RATE_BASIS_OPTIONS,
  TDS_SECTION_OPTIONS,
  getCategoryMeta,
  validatePhotoFile,
  uploadItemPhoto,
  setItemPhoto,
  saveItemExtras,
  isDuplicateName,
} from "./ItemMasterService";

export default function AddItemModal({
  isOpen,
  onClose,
  EditItemData = null,
  setState,
  getItems,
  itemList = [],
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
    reset,
    setValue,
  } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [itemState, setItemState] = useState({
    expenseHeadList: [],
    type: "",
  });
  const [photo, setPhoto] = useState({
    previewUrl: null,
    file: null,
    error: "",
    removed: false,
  });
  const fileInputRef = useRef(null);

  const categoryMeta = getCategoryMeta(itemState.type || "General");
  const isService = itemState.type === "Service";
  const isSupply = itemState.type === "Supply";

  const closeAndReset = () => {
    clearErrors();
    reset();
    setStep(1);
    setItemState({ expenseHeadList: [], type: "" });
    setPhoto({ previewUrl: null, file: null, error: "", removed: false });
    setState((pre) => ({ ...pre, EditItemData: null }));
    onClose();
  };

  const onSubmit = (data) => {
    // Fields the current API stores
    const postData = {
      expenseHeadId: data?.expenseHeadId,
      name: data?.name,
      hsnCode: data?.hsnCode,
      unit: data?.unit,
      ...(itemState.type === "Supply" && { stockType: data?.stockType }),
      // New fields — sent for forward-compatibility; backend ignores them
      // until the API is extended (see "Backend changes required")
      category: categoryMeta.key,
      gst: data?.gst,
      standardRate: data?.standardRate,
      description: data?.description,
      ...(isSupply && {
        make: data?.make,
        model: data?.model,
        minStock: data?.minStock,
        isSerialized: !!data?.isSerialized,
        warrantyMonths: data?.warrantyMonths,
      }),
      ...(isService && {
        rateBasis: data?.rateBasis,
        tdsSection: data?.tdsSection,
      }),
    };
    const option = {
      method: "POST",
      data: postData,
    };
    setIsLoading(true);

    const endPoint =
      EditItemData !== null
        ? apiUrl.update_items + `?itemId=${EditItemData?.id}`
        : apiUrl.add_items;

    callApi(endPoint, option)
      .then(async (res) => {
        if (res?.status === true) {
          // Mock layer: persist the fields the API does not store yet
          const itemId =
            EditItemData?.id ?? res?.data?.item?.id ?? res?.data?.id ?? null;
          if (itemId) {
            saveItemExtras(itemId, {
              gst: data?.gst,
              standardRate: data?.standardRate,
              description: data?.description,
              make: data?.make,
              model: data?.model,
              minStock: data?.minStock,
              isSerialized: !!data?.isSerialized,
              warrantyMonths: data?.warrantyMonths,
              rateBasis: data?.rateBasis,
              tdsSection: data?.tdsSection,
            });
            if (photo.file) {
              try {
                const url = await uploadItemPhoto(photo.file);
                setItemPhoto(itemId, url);
              } catch (e) {
                toast.error(e.message);
              }
            } else if (photo.removed) {
              setItemPhoto(itemId, null);
            }
          }
          toast.success(res?.message);
          getItems();
          closeAndReset();
        } else {
          toast.error(res?.message);
        }
      })
      .catch(() => {
        toast.error("An error occurred");
      })
      .finally(() => setIsLoading(false));
  };

  const GetExpenseHeadList = (type) => {
    setItemState((prevStates) => ({
      ...prevStates,
      expenseHeadList: [],
      type: type,
    }));
    let options = {
      method: "GET",
    };
    let API = apiUrl.get_expense_head + `?type=${type}`;
    callApi(API, options).then((response) => {
      if (response?.status === true) {
        setItemState((prevStates) => ({
          ...prevStates,
          expenseHeadList: response?.data?.expenseHead,
        }));
      }
    });
  };

  const selectCategory = (type) => {
    setValue("type", type);
    GetExpenseHeadList(type);
    setStep(2);
  };

  useEffect(() => {
    if (EditItemData !== null) {
      setStep(2);
      setValue("name", EditItemData?.name);
      setValue("hsnCode", EditItemData?.hsnCode);
      setValue("type", EditItemData?.expensehead?.expenseheadtype?.name);
      setValue("unit", EditItemData?.unit);
      setValue("stockType", String(EditItemData?.stockType));
      setValue("gst", EditItemData?.gst ?? "");
      setValue("standardRate", EditItemData?.standardRate ?? "");
      setValue("description", EditItemData?.description ?? "");
      setValue("make", EditItemData?.make ?? "");
      setValue("model", EditItemData?.model ?? "");
      setValue("minStock", EditItemData?.minStock ?? "");
      setValue("isSerialized", !!EditItemData?.isSerialized);
      setValue("warrantyMonths", EditItemData?.warrantyMonths ?? "");
      setValue("rateBasis", EditItemData?.rateBasis ?? "");
      setValue("tdsSection", EditItemData?.tdsSection ?? "");
      setPhoto({
        previewUrl: EditItemData?.photoUrl || null,
        file: null,
        error: "",
        removed: false,
      });
      GetExpenseHeadList(EditItemData?.expensehead?.expenseheadtype?.name);
    } else {
      reset();
      setStep(1);
      setItemState({ expenseHeadList: [], type: "" });
      setPhoto({ previewUrl: null, file: null, error: "", removed: false });
    }
  }, [EditItemData, isOpen]);

  useEffect(() => {
    if (EditItemData !== null) {
      setValue("expenseHeadId", EditItemData?.expenseHeadId);
    }
  }, [itemState?.expenseHeadList]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validatePhotoFile(file);
    if (error) {
      toast.error(error);
      setPhoto((pre) => ({ ...pre, error }));
      e.target.value = "";
      return;
    }
    setPhoto({
      previewUrl: URL.createObjectURL(file),
      file,
      error: "",
      removed: false,
    });
    e.target.value = "";
  };

  const handlePhotoRemove = () => {
    setPhoto({ previewUrl: null, file: null, error: "", removed: true });
  };

  const isEdit = EditItemData !== null;

  return (
    <Modal
      className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg"
      isOpen={isOpen}
      toggle={closeAndReset}
    >
      <div className="modal-content">
        <div className="modal-header p-4 border-0">
          <h4
            className="modal-title rajdhani-semibold"
            id="attendanceModalLabel"
          >
            {isEdit
              ? "Edit Item"
              : step === 1
                ? "Add Item — Select Category"
                : `Add ${categoryMeta.label} Item`}
          </h4>
          <button type="button" className="btn-close" onClick={closeAndReset} />
        </div>
        <div className="modal-body p-4">
          {/* ---------------- STEP 1 : category cards ---------------- */}
          {!isEdit && step === 1 && (
            <div className="row g-3">
              {ITEM_CATEGORIES.map((cat) => (
                <div className="col-12 col-md-4" key={cat.key}>
                  <button
                    type="button"
                    className="erp-category-card"
                    onClick={() => selectCategory(cat.type)}
                  >
                    <div
                      className={`erp-item-photo-fallback erp-category-card-icon ${cat.key}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                        <path d="m3.3 7 8.7 5 8.7-5" />
                        <path d="M12 22V12" />
                      </svg>
                    </div>
                    <h5 className="rajdhani-semibold">{cat.label}</h5>
                    <p>{cat.description}</p>
                    <span className="erp-category-prefix">{cat.prefix}-</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ---------------- STEP 2 : item form ---------------- */}
          {(isEdit || step === 2) && (
            <form
              className="row"
              onSubmit={handleSubmit(onSubmit)}
              id="ItemRequest"
            >
              <input type="hidden" {...register("type")} />

              {/* Category (read-only) + item code on edit */}
              <div className={isEdit ? "col-6" : "col-12"}>
                <div className="form-group mb-3">
                  <label className="form-label">Category</label>
                  <input
                    className="form-control"
                    value={categoryMeta.label}
                    disabled
                    readOnly
                  />
                </div>
              </div>
              {isEdit && (
                <div className="col-6">
                  <div className="form-group mb-3">
                    <label className="form-label">Item Code</label>
                    <input
                      className="form-control"
                      value={EditItemData?.code || "--"}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              )}

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="itemName" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    id="itemName"
                    placeholder="Enter Name here..."
                    maxLength={160}
                    {...register("name", {
                      required: "Name is required",
                      maxLength: {
                        value: 160,
                        message: "Name cannot exceed 160 characters",
                      },
                      validate: (value) =>
                        !isDuplicateName(value, itemList, EditItemData?.id) ||
                        "An item with this name already exists",
                    })}
                  />
                  <ErrorMessage message={errors?.name?.message} />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="expenseHeadId" className="form-label">
                    {categoryMeta.groupLabel}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="expenseHeadId"
                    {...register("expenseHeadId", {
                      required: `${categoryMeta.groupLabel} is required`,
                    })}
                    disabled={itemState?.expenseHeadList?.length === 0}
                  >
                    <option value="" className="d-none">
                      Select {categoryMeta.groupLabel}
                    </option>
                    {itemState?.expenseHeadList?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage message={errors?.expenseHeadId?.message} />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    Unit (UOM) <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("unit", { required: "Unit is required" })}
                  >
                    <option value="" hidden>
                      Select Unit
                    </option>
                    {UnitCollection?.map((unit, index) => {
                      return (
                        <option key={index} value={unit.value}>
                          {unit.label}
                        </option>
                      );
                    })}
                  </select>
                  <ErrorMessage message={errors?.unit?.message} />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    {categoryMeta.taxCodeLabel}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    placeholder={`Enter ${categoryMeta.taxCodeLabel} here...`}
                    {...register("hsnCode", {
                      required: `${categoryMeta.taxCodeLabel} is required`,
                      pattern: {
                        value: /^[a-zA-Z0-9]+$/,
                        message: "Only alphanumeric characters are allowed",
                      },
                    })}
                  />
                  <ErrorMessage message={errors?.hsnCode?.message} />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    GST % <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("gst", { required: "GST % is required" })}
                  >
                    <option value="" hidden>
                      Select GST %
                    </option>
                    {GST_OPTIONS.map((gst) => (
                      <option key={gst} value={gst}>
                        {gst}%
                      </option>
                    ))}
                  </select>
                  <ErrorMessage message={errors?.gst?.message} />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">Standard Rate (₹)</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter standard rate..."
                    {...register("standardRate", {
                      min: { value: 0, message: "Rate cannot be negative" },
                    })}
                  />
                  {isEdit && (
                    <small className="text-muted">
                      Note: rate changes are logged
                    </small>
                  )}
                  <ErrorMessage message={errors?.standardRate?.message} />
                </div>
              </div>

              {/* ------- Supply-only section ------- */}
              {isSupply && (
                <>
                  <div className="col-12">
                    <hr className="my-2" />
                    <p className="text-muted text-uppercase mb-2" style={{ fontSize: "11px", fontWeight: 700 }}>
                      Supply Details
                    </p>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Make</label>
                      <input
                        className="form-control"
                        placeholder="Enter make..."
                        {...register("make")}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Model</label>
                      <input
                        className="form-control"
                        placeholder="Enter model..."
                        {...register("model")}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group mb-3">
                      <label className="form-label">Min Stock</label>
                      <input
                        className="form-control"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...register("minStock", {
                          min: { value: 0, message: "Cannot be negative" },
                        })}
                      />
                      <ErrorMessage message={errors?.minStock?.message} />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group mb-3">
                      <label className="form-label">Warranty (months)</label>
                      <input
                        className="form-control"
                        type="number"
                        min="0"
                        placeholder="0"
                        {...register("warrantyMonths", {
                          min: { value: 0, message: "Cannot be negative" },
                        })}
                      />
                      <ErrorMessage message={errors?.warrantyMonths?.message} />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="form-group mb-3">
                      <label className="form-label d-block">Serialized</label>
                      <label className="d-flex gap-2 align-items-center mt-2">
                        <input type="checkbox" {...register("isSerialized")} />
                        <span>Track serial numbers</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group d-flex mb-3 justify-content-between">
                      <label className="form-label">
                        Stock Type <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex justify-content-between gap-4">
                        <div className="form-group">
                          <label className="d-flex gap-2">
                            <input
                              type="radio"
                              value={0}
                              {...register("stockType", {
                                required: "Stock Type is required",
                              })}
                            />
                            <span>Fixed Assets</span>
                          </label>
                        </div>
                        <div className="form-group">
                          <label className="d-flex gap-2">
                            <input
                              type="radio"
                              value={1}
                              {...register("stockType")}
                            />
                            <span>Consumeable Assets</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <ErrorMessage message={errors?.stockType?.message} />
                  </div>
                </>
              )}

              {/* ------- Service-only section ------- */}
              {isService && (
                <>
                  <div className="col-12">
                    <hr className="my-2" />
                    <p className="text-muted text-uppercase mb-2" style={{ fontSize: "11px", fontWeight: 700 }}>
                      Service Details
                    </p>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Rate Basis</label>
                      <select className="form-select" {...register("rateBasis")}>
                        <option value="" hidden>
                          Select Rate Basis
                        </option>
                        {RATE_BASIS_OPTIONS.map((basis) => (
                          <option key={basis} value={basis}>
                            {basis}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">TDS Section</label>
                      <select
                        className="form-select"
                        {...register("tdsSection")}
                      >
                        <option value="" hidden>
                          Select TDS Section
                        </option>
                        {TDS_SECTION_OPTIONS.map((section) => (
                          <option key={section} value={section}>
                            {section}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="col-12">
                <div className="form-group mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    maxLength={500}
                    placeholder="Enter description..."
                    {...register("description")}
                  />
                </div>
              </div>

              {/* ------- Photo upload (mocked until upload API exists) ------- */}
              <div className="col-12">
                <div className="form-group mb-3">
                  <label className="form-label">Photo</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="d-none"
                    onChange={handlePhotoSelect}
                  />
                  {photo.previewUrl ? (
                    <div className="erp-photo-preview d-flex align-items-end gap-3">
                      <img src={photo.previewUrl} alt="item" />
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handlePhotoRemove}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="erp-photo-upload"
                      role="button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <p>
                        Click to upload — JPG, PNG or WebP, max 6MB
                      </p>
                    </div>
                  )}
                  <ErrorMessage message={photo.error} />
                </div>
              </div>
            </form>
          )}
        </div>
        <div className="modal-footer p-4 border-0">
          {!isEdit && step === 2 && (
            <button
              type="button"
              className="btn btn-secondary me-auto"
              onClick={() => setStep(1)}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeAndReset}
          >
            Close
          </button>
          {(isEdit || step === 2) && (
            <button
              type="submit"
              className="btn btn-primary"
              form="ItemRequest"
            >
              {isLoading ? (
                <Spinner />
              ) : isEdit ? (
                "Update Item"
              ) : (
                "Add Item"
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
